import React from "react"
import { IconButton } from "@chakra-ui/react"
import { Control } from "react-hook-form"
import { BadgeTemplate, TransformStyle } from "sharedTypes/badge"
import { useGetDocument, useGetSelection } from "components/CanvasEditor/Editor"
import {
  RotateCWIcon,
  FlipHorizontal2Icon,
  FlipVertical2Icon,
} from "components/BadgeBuilder/Icons"
import EditorPane from "components/CanvasEditor/EditorPane"
import PropertyRow from "components/CanvasEditor/Inspector/PropertyRow"
import NumberProperty from "components/CanvasEditor/Inspector/NumberProperty"
import { BadgeElementFormValues } from "components/BadgeBuilder/Panels/EditorPanel/BadgeSidePanelForm"
import { normalizeRotation } from "components/BadgeBuilder/utils/elementDisplay"
import { useBadgeElement } from "components/BadgeBuilder/contexts/BadgeElementContext"

type Props = {
  control: Control<BadgeElementFormValues>
  hideFlipControls?: boolean
  /** Instead of setting the field: turn by this many degrees (a group, as a whole) */
  onRotateBy?: (degrees: number) => void
  /** Instead of setting the field: flip (a group, as a whole) */
  onFlip?: (axis: "horizontal" | "vertical") => void
}

const NO_TRANSFORMS: TransformStyle = { rotate: 0, flipHorizontal: false, flipVertical: false }

const TransformControls = ({
  control, hideFlipControls = false, onRotateBy, onFlip,
}: Props) => {
  const { handleElementChange } = useBadgeElement()
  const getTemplate = useGetDocument<BadgeTemplate>()
  const getSelection = useGetSelection()

  // The buttons change the element, not the field, so the field only ever shows what the element
  // has (a turn that doesn't fit on the badge is refused without the field flickering)
  const changeElement = (change: (transforms: TransformStyle) => TransformStyle) => {
    const [id] = getSelection().selectedIds
    const element = getTemplate().badgeData.elements.find((el) => el.id === id)
    if (!element) return

    handleElementChange(id, { transforms: change(element.transforms ?? NO_TRANSFORMS) })
  }

  const rotateBy = onRotateBy ?? ((degrees: number) => changeElement((transforms) => ({
    ...transforms, rotate: normalizeRotation(transforms.rotate + degrees),
  })))
  const flip = onFlip ?? ((axis: "horizontal" | "vertical") => changeElement((transforms) => (
    axis === "horizontal"
      ? { ...transforms, flipHorizontal: !transforms.flipHorizontal }
      : { ...transforms, flipVertical: !transforms.flipVertical }
  )))

  return (
    <EditorPane title="Transform">
      <PropertyRow label="Rotate" rightAlign>
        <NumberProperty
          name="transforms.rotate"
          control={control}
          angle
          step={1}
          precision={0}
          size="sm"
        />
        <IconButton
          aria-label="Rotate Counter-Clockwise (-45°)"
          icon={<RotateCWIcon style={{ transform: "scaleX(-1)" }} />}
          size="sm"
          variant="outline"
          bg="white"
          onClick={() => rotateBy(-45)}
          title="Rotate Counter-Clockwise (-45°)"
        />
        <IconButton
          aria-label="Rotate Clockwise (+45°)"
          icon={<RotateCWIcon />}
          size="sm"
          variant="outline"
          bg="white"
          onClick={() => rotateBy(45)}
          title="Rotate Clockwise (+45°)"
        />
      </PropertyRow>

      {!hideFlipControls && (
        <PropertyRow label="Flip" rightAlign>
          <IconButton
            aria-label="Flip Horizontal"
            icon={<FlipHorizontal2Icon />}
            size="sm"
            variant="outline"
            bg="white"
            onClick={() => flip("horizontal")}
            title="Flip Horizontal"
          />
          <IconButton
            aria-label="Flip Vertical"
            icon={<FlipVertical2Icon />}
            size="sm"
            variant="outline"
            bg="white"
            onClick={() => flip("vertical")}
            title="Flip Vertical"
          />
        </PropertyRow>
      )}
    </EditorPane>
  )
}

export default TransformControls
